import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { User, Lock, LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { clsx } from 'clsx';

type Stats = { properties: number; agencies: number; portals: number };

function getGreeting(): { greeting: string; subtitle: string } {
  const h = new Date().getHours();
  if (h >= 5 && h < 12)  return { greeting: 'Buenos días.',   subtitle: 'Empieza el día con una nueva propiedad.' };
  if (h >= 12 && h < 19) return { greeting: 'Buenas tardes.', subtitle: 'Vamos por el siguiente cierre.' };
  return                       { greeting: 'Buenas noches.', subtitle: 'Aún hay leads esperando una respuesta.' };
}

function useCountUp(target: number | null, duration = 1200): number {
  const [value, setValue] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === null) return;
    let raf: number;
    startRef.current = null;

    const tick = (ts: number) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
}

export const LoginView: React.FC<{ theme?: 'light' | 'dark' | 'high-contrast', skin?: string }> = ({ theme = 'light', skin = 'ocean' }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);

  const isLight = theme !== 'dark';
  const { greeting, subtitle } = getGreeting();

  const propsCount    = useCountUp(stats?.properties ?? null);
  const agenciesCount = useCountUp(stats?.agencies   ?? null);
  const portalsCount  = useCountUp(stats?.portals    ?? null);

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    supabase.rpc('public_login_stats').then(({ data }) => {
      if (cancelled || !data) return;
      setStats(data as Stats);
    });
    return () => { cancelled = true; };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError('Supabase client is not configured.');
      return;
    }

    setLoading(true);
    setError(null);

    let email = identifier.trim();

    if (!email.includes('@')) {
      const { data, error: rpcError } = await supabase.rpc('lookup_email_by_username', {
        p_username: email,
      });

      if (rpcError || !data) {
        setError('Usuario no encontrado');
        setLoading(false);
        return;
      }
      email = data;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError('Credenciales incorrectas');
    }
    setLoading(false);
  };

  const stagger = (i: number): React.CSSProperties => ({
    animationDelay: `${i * 80}ms`,
    opacity: 0,
    animationFillMode: 'forwards',
  });

  const fmt = (n: number) => n.toLocaleString('es-GT');

  return (
    <div className={clsx(
      "min-h-screen flex flex-col md:flex-row relative overflow-hidden",
      `skin-${skin}`,
      isLight ? "light-theme bg-[#f8fafc]" : "dark-theme bg-[#020617]"
    )}>
      {/* Left panel: form */}
      <div className="relative z-10 flex w-full md:w-1/2 items-center justify-center p-6 md:p-12">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-indigo-500/10 blur-[120px] rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-purple-500/10 blur-[100px] rounded-full translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="relative z-10 w-full max-w-sm">
          <div className="mb-10 animate-slide-up" style={stagger(0)}>
            <img
              src="/intver-logo.png"
              alt="Intercambio Inmobiliario"
              className="w-14 h-14 rounded-2xl object-cover mb-6 shadow-lg shadow-sky-500/30"
            />
            <h1 className={clsx("text-3xl font-black tracking-tight", isLight ? "text-slate-900" : "text-white")}>
              Inicia sesión
            </h1>
            <p className={clsx("text-sm font-medium mt-2", isLight ? "text-slate-500" : "text-slate-400")}>
              Ingresa tus credenciales para acceder a tu cuenta.
            </p>
          </div>

          {error && (
            <div className="mb-6 flex items-start gap-3 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 animate-fade-in">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <p className="text-xs font-bold leading-relaxed">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1 animate-slide-up" style={stagger(1)}>
              <label className={clsx("text-[10px] font-black uppercase tracking-widest pl-1", isLight ? "text-slate-500" : "text-slate-400")}>Usuario o Email</label>
              <div className={clsx(
                "flex items-center gap-3 p-3.5 rounded-2xl border transition-all focus-within:border-indigo-500",
                isLight ? "bg-white border-slate-200 text-slate-900 shadow-sm" : "bg-black/20 border-white/10 text-white focus-within:bg-black/40"
              )}>
                <User size={18} className="text-indigo-500" />
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="tu_usuario o email"
                  className="bg-transparent focus:outline-none w-full text-sm font-medium"
                  required
                />
              </div>
            </div>

            <div className="space-y-1 animate-slide-up" style={stagger(2)}>
              <label className={clsx("text-[10px] font-black uppercase tracking-widest pl-1", isLight ? "text-slate-500" : "text-slate-400")}>Contraseña</label>
              <div className={clsx(
                "flex items-center gap-3 p-3.5 rounded-2xl border transition-all focus-within:border-indigo-500",
                isLight ? "bg-white border-slate-200 text-slate-900 shadow-sm" : "bg-black/20 border-white/10 text-white focus-within:bg-black/40"
              )}>
                <Lock size={18} className="text-indigo-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="bg-transparent focus:outline-none w-full text-sm font-medium"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className={clsx(
                    "shrink-0 p-1 rounded-lg transition-colors",
                    isLight ? "text-slate-400 hover:text-indigo-500" : "text-slate-500 hover:text-indigo-400"
                  )}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end animate-fade-in" style={stagger(3)}>
              <Link to="/recuperar-contrasena" className={clsx("text-xs font-bold transition-colors", isLight ? "text-indigo-600 hover:text-indigo-500" : "text-indigo-400 hover:text-indigo-300")}>
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={stagger(4)}
              className="w-full mt-2 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-black text-sm transition-all shadow-xl shadow-indigo-600/20 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:shadow-none animate-slide-up"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              ) : (
                <>
                  <LogIn size={18} /> Entrar
                </>
              )}
            </button>
          </form>

          <p className={clsx(
            "text-center text-xs font-medium mt-8 animate-fade-in",
            isLight ? "text-slate-500" : "text-slate-400"
          )}
          style={stagger(6)}>
            ¿No tienes cuenta?{' '}
            <Link to="/planes" className={clsx("font-bold transition-colors", isLight ? "text-indigo-600 hover:text-indigo-500" : "text-indigo-400 hover:text-indigo-300")}>
              Ver planes
            </Link>
            <br />
            ¿Solo quieres tomar cursos?{' '}
            <Link to="/academia/registro" className={clsx("font-bold transition-colors", isLight ? "text-indigo-600 hover:text-indigo-500" : "text-indigo-400 hover:text-indigo-300")}>
              Crea tu cuenta de Academia
            </Link>
          </p>
        </div>
      </div>

      {/* Right panel: brand */}
      <div className="relative hidden md:flex md:w-1/2 items-center justify-center p-12 bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-400/20 blur-[120px] rounded-full translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-300/20 blur-[100px] rounded-full -translate-x-1/3 translate-y-1/3" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.05)_0%,transparent_50%)]" />
        </div>

        <div className="relative z-10 max-w-md text-white">
          {/* Logo with network ripples */}
          <div className="relative w-20 h-20 mb-10 animate-slide-up" style={stagger(0)}>
            <span
              className="absolute top-1/2 left-1/2 w-20 h-20 rounded-full border-2 border-white/40 animate-ripple pointer-events-none"
              style={{ animationDelay: '0s' }}
            />
            <span
              className="absolute top-1/2 left-1/2 w-20 h-20 rounded-full border-2 border-white/30 animate-ripple pointer-events-none"
              style={{ animationDelay: '0.9s' }}
            />
            <span
              className="absolute top-1/2 left-1/2 w-20 h-20 rounded-full border-2 border-white/20 animate-ripple pointer-events-none"
              style={{ animationDelay: '1.8s' }}
            />
            <img
              src="/intver-logo.png"
              alt="Intercambio Inmobiliario"
              className="relative w-20 h-20 rounded-2xl object-cover shadow-2xl shadow-sky-500/40 ring-1 ring-white/20"
            />
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-6 animate-slide-up" style={stagger(1)}>
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest">Plataforma activa en Guatemala</span>
          </div>

          <h2 className="text-6xl font-black tracking-tight leading-[0.95] mb-4 animate-slide-up" style={stagger(2)}>
            {greeting}
          </h2>
          <p className="text-base text-indigo-100/80 leading-relaxed mb-12 animate-slide-up" style={stagger(3)}>
            {subtitle}
          </p>

          {/* Live stats */}
          <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10">
            {[
              { value: propsCount,    label: 'Propiedades', sub: 'publicadas',    ready: stats !== null, delay: 4 },
              { value: agenciesCount, label: 'Agencias',    sub: 'conectadas',    ready: stats !== null, delay: 5 },
              { value: portalsCount,  label: 'Portales',    sub: 'públicos',      ready: stats !== null, delay: 6 },
            ].map((s) => (
              <div key={s.label} className="animate-slide-up" style={stagger(s.delay)}>
                <p className="text-3xl font-black tracking-tight tabular-nums">
                  {s.ready ? fmt(s.value) : '—'}
                </p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-100/60 mt-1">{s.label}</p>
                <p className="text-[10px] text-indigo-100/50">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
